import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tControlFunction, tControlStatus,
  CONTROL_FUNCTIONS, CONTROL_FUNCTION_KEYS, CONTROL_STATUS, CONTROL_STATUS_KEYS,
  EFFECTIVENESS, EFFECTIVENESS_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { EffBadge } from "@/components/shared/ControlsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, Plus, Search } from "lucide-react";

const EMPTY = { name: "", description: "", function: "preventive", status: "DRAFT", owner: "", implemented: false };

export default function Controls() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [fFn, setFFn] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fEff, setFEff] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const writable = can("control:write");

  const { data: controls = [], isLoading } = useQuery({
    queryKey: ["controls", q, fFn, fStatus, fEff],
    queryFn: async () => {
      const params = {};
      if (q) params.q = q;
      if (fFn !== "all") params.function = fFn;
      if (fStatus !== "all") params.status = fStatus;
      if (fEff !== "all") params.effectiveness = fEff;
      return (await api.get("/controls", { params })).data;
    },
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/controls", form)).data,
    onSuccess: () => { toast.success("Contrôle créé"); setOpen(false); qc.invalidateQueries({ queryKey: ["controls"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <AppShell title="Contrôles"
      subtitle={`${controls.length} contrôle(s) — objet dédié · présence ≠ réduction de risque`}
      actions={writable && (
        <Button size="sm" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="create-control-button">
          <Plus className="h-4 w-4" /> Nouveau contrôle
        </Button>
      )}
    >
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="control-filters">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom)…" className="pl-9 rounded-sm" data-testid="control-search-input" />
        </div>
        <FilterSelect label="Fonction" value={fFn} onChange={setFFn} testid="control-filter-function" options={CONTROL_FUNCTION_KEYS.map((k) => [k, CONTROL_FUNCTIONS[k]])} />
        <FilterSelect label="Statut" value={fStatus} onChange={setFStatus} testid="control-filter-status" options={CONTROL_STATUS_KEYS.map((k) => [k, CONTROL_STATUS[k].label])} />
        <FilterSelect label="Efficacité" value={fEff} onChange={setFEff} testid="control-filter-effectiveness" options={EFFECTIVENESS_KEYS.map((k) => [k, EFFECTIVENESS[k].label])} />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : controls.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun contrôle. Créez-en un ou ajustez les filtres.</p>
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="controls-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Nom</TableHead>
                <TableHead>Fonction</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Efficacité (attestée)</TableHead>
                <TableHead className="text-right">Portée</TableHead>
                <TableHead className="text-right">Techniques</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {controls.map((c) => (
                <TableRow key={c.id} className="border-border cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/controls/${c.id}`)} data-testid={`control-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{tControlFunction(c.function)}</TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-sm text-[10px] ${CONTROL_STATUS[c.status]?.cls || ""}`}>{tControlStatus(c.status)}</Badge></TableCell>
                  <TableCell><EffBadge eff={c.effectiveness} /></TableCell>
                  <TableCell className="text-right"><Badge variant="outline" className="rounded-sm font-mono text-xs">{c.protect_count ?? 0}</Badge></TableCell>
                  <TableCell className="text-right"><Badge variant="outline" className="rounded-sm font-mono text-xs">{c.technique_count ?? 0}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="control-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouveau contrôle</DialogTitle>
            <DialogDescription>L'efficacité n'est jamais saisie ici : elle s'atteste séparément (CISO). Un contrôle déclaré ne réduit aucun risque.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="control-name-input" />
            </div>
            <div>
              <Label className="text-xs">Fonction</Label>
              <Select value={form.function} onValueChange={(v) => setForm({ ...form, function: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="control-function-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTROL_FUNCTION_KEYS.map((k) => <SelectItem key={k} value={k}>{CONTROL_FUNCTIONS[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut (administratif)</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="control-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTROL_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{CONTROL_STATUS[k].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Responsable</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" id="impl" checked={form.implemented} onChange={(e) => setForm({ ...form, implemented: e.target.checked })} data-testid="control-implemented-checkbox" />
              <Label htmlFor="impl" className="text-xs">Déployé (implémenté)</Label>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => create.mutate()} disabled={!form.name || create.isPending} data-testid="save-control-button">
              {create.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-sm w-[180px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous</SelectItem>
          {options.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
